import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { gallery3, gallery4, gallery5, gallery6, gallery7, gallery10, gallery11, gallery12, gallery13, gallery14, gallery15, gallery8, gallery9 } from '../assets';
import { FaPlay } from 'react-icons/fa';

const Gallery = ({ audioRef }) => {
  // Sample images - replace with actual wedding photos
  const images = [
    {
      url: gallery3,
      caption: 'Our Engagement Day',
      colspan: {
        md: 'md:col-span-3 max-h-[398px]',
        sm: ''
      },
      aspect: ''
    },
    {
      url: gallery4,
      caption: 'Beautiful Moments',
      colspan: {
        md: '',
        sm: ''
      },
      aspect: ''
    },
    {
      url: gallery5,
      caption: 'Together Forever',
      colspan: {
        md: 'md:col-span-2',
        sm: 'col-span-2'
      },
      aspect: ''
    },
    {
      url: gallery6,
      caption: 'Love Story',
      colspan: {
        md: 'md:col-span-1',
        sm: 'col-span-2 hidden'
      },
      aspect: ''
    },
    {
      url: gallery7,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:col-span-1',
        sm: 'col-span-2'
      },
      aspect: ''
    },
    {
      url: gallery8,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:col-span-1',
        sm: 'col-span-2'
      },
      aspect: 'aspect-auto'
    },
    {
      url: gallery9,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: ''
      },
      aspect: ''
    },
    {
      url: gallery10,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: ''
      },
      aspect: ''
    },
    {
      url: gallery11,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: ''
      },
      aspect: ''
    },
    {
      url: gallery12,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: 'col-span-2'
      },
      aspect: ''
    },
    {
      url: gallery13,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: 'col-span-2'
      },
      aspect: ''
    },
    {
      url: gallery14,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: 'col-span-2'
      },
      aspect: ''
    },
    {
      url: gallery15,
      caption: 'Perfect Day',
      colspan: {
        md: 'md:',
        sm: 'col-span-2'
      },
      aspect: ''
    }
  ];

  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Handle video play/pause
  const handleVideoClick = () => {
    setIsVideoPlaying(true);

    // Pause background music when video starts
    if (audioRef?.current) {
      audioRef.current.pause();
    }
  };

  const youtubeVideoId = 'D8nsdxuV3s0';

  // Listen for video end event to resume music
  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      const handleVideoEnd = () => {
        setIsVideoPlaying(false);
        // Resume music when video ends
        if (audioRef.current) {
          audioRef.current.play();
        }
      };

      video.addEventListener('ended', handleVideoEnd);

      return () => {
        video.removeEventListener('ended', handleVideoEnd);
      };
    }
  }, []);

  return (
    <section className="py-32 px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-7xl mb-6">Our Memories</h2>
          <p className="text-lg text-sage mt-8">
            A glimpse into our journey together
          </p>
        </motion.div>

        {/* Video Section */}
        <motion.div
          className="mb-20 relative -mx-4 md:mx-0"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          {/* Full width container */}
          <div className="relative w-full md:rounded-3xl overflow-hidden shadow-2xl" style={{ paddingTop: '56.25%' }}>
            {isVideoPlaying ? (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1&controls=1`}
                title="Wedding Video"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              ></iframe>
            ) : (
              <motion.div
                className="absolute inset-0 cursor-pointer"
                onClick={handleVideoClick}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.6 }}
              >
                {/* Thumbnail */}
                <img
                  src={`https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`}
                  alt="Wedding Video"
                  className="w-full h-full object-cover"
                />

                {/* Cinematic overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                {/* Centered content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-4">
                  {/* Play button with ring */}
                  <motion.div
                    className="relative"
                    whileHover={{ scale: 1.1 }}
                  >
                    {/* Animated ring */}
                    <motion.div
                      className="absolute -inset-4 rounded-full border-2 border-white/40"
                      animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />

                    {/* Button */}
                    <div className="w-16 h-16 md:w-28 md:h-28 rounded-full bg-white flex items-center justify-center shadow-2xl">
                      <FaPlay className="text-md md:text-3xl text-sage ml-1.5" />
                    </div>
                  </motion.div>

                  {/* Title */}
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <h3 className="text-2xl md:text-5xl text-white font-display mb-2">
                      Caleb & Alyssa
                    </h3>
                    <p className="text-white/90 text-sm md:text-xl font-light">
                      Our Story Begins
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Thumbnail Gallery */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {images.slice(0, 6).map((image, index) => (
            <div
              key={index}
              className={`relative overflow-hidden rounded shadow-lg group cursor-pointer
                ${image.colspan > 1 && image.aspect === '' ? "aspect-video" : "aspect-auto"}
                ${image.colspan.md} ${image.colspan.sm}
                `}
            >
              <img
                src={image.url}
                alt={image.caption}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent flex items-end justify-center p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white font-display text-lg italic">{image.caption}</span>
              </div> */}
            </div>
          ))}
        </motion.div>
      </div>

      <style jsx>
        {`
        .wedding-swiper :global(.swiper-button-next),
        .wedding-swiper :global(.swiper-button-prev) {
          color: white;
          background: rgba(156, 169, 134, 0.8);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .wedding-swiper :global(.swiper-button-next::after),
        .wedding-swiper :global(.swiper-button-prev::after) {
          font-size: 20px;
        }

        .wedding-swiper :global(.swiper-button-next:hover),
        .wedding-swiper :global(.swiper-button-prev:hover) {
          background: rgba(107, 122, 91, 1);
          transform: scale(1.1);
        }

        .wedding-swiper :global(.swiper-pagination-bullet) {
          width: 12px;
          height: 12px;
          background: white;
          opacity: 0.5;
          transition: all 0.3s ease;
        }

        .wedding-swiper :global(.swiper-pagination-bullet-active) {
          background: #C9A961;
          opacity: 1;
          width: 30px;
          border-radius: 6px;
        }
      `}
      </style>
    </section>
  );
};

export default Gallery;
